import { useEffect, useState } from "react";
import { Page, Card, Field, Input, Select, Button, Money, Badge, Feedback, Table, Empty } from "../../ui";
import { send, del, entityRows } from "../../lib/api";
import { useVendors, useAccounts } from "../../lib/hooks";
import { statusTone, today } from "../../lib/format";
import { t } from "../../i18n";

const BU = "NUMEN";
function genId(p: string) { return p + String(Date.now()).slice(-7); }

/* ── Purchasing: PO → receipt → 3-way match ───────────────────────────── */
export function PoPage() {
  const vendors = useVendors();
  const accounts = useAccounts().filter((a) => a.acct_type === "E");
  const [rows, setRows] = useState<Record<string, string>[]>([]);
  const [fb, setFb] = useState({ ok: null as boolean | null, msg: "" });
  const [vendor, setVendor] = useState(""); const [acct, setAcct] = useState("");
  const [qty, setQty] = useState("1"); const [price, setPrice] = useState("");
  const load = () => entityRows<Record<string, string>>("ps_purchase_order").then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const create = async () => {
    setFb({ ok: null, msg: "" });
    try {
      const id = genId("PO");
      await send("POST", "/api/v1/po/orders", {
        business_unit: BU, po_id: id, vendor_id: vendor, po_dt: today(), currency_cd: "CAD",
        lines: [{ po_line_nbr: 1, quantity_ordered: qty, unit_price: Number(price).toFixed(2), expense_account: acct }],
      }, true);
      setFb({ ok: true, msg: t("po.created", { id }) }); setPrice(""); load();
    } catch (e) { setFb({ ok: false, msg: String((e as Error).message) }); }
  };
  const match = async (po: string) => {
    try { const r = await send<{ match_status: string }>("POST", `/api/v1/po/orders/${po}/match?bu=${BU}`); setFb({ ok: r.match_status === "MATCHED", msg: t("po.matchResult", { po, status: r.match_status }) }); load(); }
    catch (e) { setFb({ ok: false, msg: String((e as Error).message) }); }
  };
  // receive fetches the PO's lines and records a goods receipt for the full
  // ordered quantity (feeds the 3-way match).
  const receive = async (po: Record<string, string>) => {
    try {
      const allLines = await entityRows<Record<string, string>>("ps_po_line");
      const lines = allLines.filter((l) => l.po_id === po.po_id).map((l) => ({
        po_line_nbr: Number(l.po_line_nbr), quantity_received: l.quantity_ordered, unit_price: l.unit_price,
      }));
      if (!lines.length) { setFb({ ok: false, msg: t("po.noLines") }); return; }
      await send("POST", "/api/v1/po/receipts", {
        business_unit: BU, receipt_id: genId("RCV"), po_id: po.po_id, vendor_id: po.vendor_id,
        receipt_dt: today(), currency_cd: po.currency_cd || "CAD", lines,
      }, true);
      setFb({ ok: true, msg: t("po.received", { po: po.po_id }) }); load();
    } catch (e) { setFb({ ok: false, msg: String((e as Error).message) }); }
  };
  return (
    <Page title={t("nav.po")}>
      <div className="report-cols">
        <Card title={t("po.newOrder")}>
          <Field label={t("common.vendor")}><Select value={vendor} onChange={(e) => setVendor(e.target.value)}><option value="">{t("common.selectVendor")}</option>{vendors.map((v) => <option key={v.vendor_id} value={v.vendor_id}>{v.vendor_name}</option>)}</Select></Field>
          <Field label={t("ap.expenseAccount")}><Select value={acct} onChange={(e) => setAcct(e.target.value)}><option value="">{t("common.selectAccount")}</option>{accounts.map((a) => <option key={a.account} value={a.account}>{a.account} — {a.descr}</option>)}</Select></Field>
          <div className="form-row">
            <Field label={t("po.quantity")}><Input className="num" value={qty} onChange={(e) => setQty(e.target.value)} /></Field>
            <Field label={t("po.unitPrice")}><Input className="num" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} /></Field>
          </div>
          <div className="form-actions"><Button disabled={!vendor || !acct || !price} onClick={create}>{t("po.create")}</Button></div>
          <Feedback ok={fb.ok} message={fb.msg} />
        </Card>
        <Card title={t("po.orders")}>
          {rows.length ? (
            <Table head={[t("po.col.po"), t("common.vendor"), t("common.total"), t("po.col.match"), t("common.actions")]}>
              {rows.map((p) => (
                <tr key={p.po_id}>
                  <td>{p.po_id}</td><td>{p.vendor_id}</td><td className="num"><Money value={p.total_amt} /></td>
                  <td><Badge tone={statusTone(p.match_status)}>{p.match_status}</Badge></td>
                  <td>
                    <button className="link-btn" onClick={() => receive(p)}>{t("po.receive")}</button>
                    <button className="link-btn" onClick={() => match(p.po_id)}>{t("po.threeWayMatch")}</button>
                  </td>
                </tr>
              ))}
            </Table>
          ) : <Empty>{t("po.noOrders")}</Empty>}
        </Card>
      </div>
    </Page>
  );
}

/* ── Master-data maintenance (create / browse / delete over reference tables) ── */
// pk = the non-tenant primary-key columns, in order, used to address a row for
// delete (the entity API joins them with '|'). fields = the columns the create
// form offers (tenant_id is set server-side from the JWT).
const ENTITIES = [
  { e: "ps_account", labelKey: "ent.chartOfAccounts", group: "ent.grp.master", pk: ["setid", "account", "effdt"], fields: ["setid", "account", "descr", "descrshort", "acct_type", "effdt", "eff_status"] },
  { e: "ps_customer", labelKey: "ent.customers", group: "ent.grp.master", pk: ["setid", "customer_id"], fields: ["setid", "customer_id", "customer_name", "customer_status", "currency_cd", "country"] },
  { e: "ps_vendor", labelKey: "ent.vendors", group: "ent.grp.master", pk: ["setid", "vendor_id"], fields: ["setid", "vendor_id", "vendor_name", "vendor_status", "currency_cd"] },
  { e: "ps_currency", labelKey: "ent.currencies", group: "ent.grp.master", pk: ["currency_cd"], fields: ["currency_cd", "descr", "descrshort", "symbol", "eff_status"] },
  // Restored sub-ledger collections (Epic 19.4) — maintained via the generic grid.
  { e: "ps_ar_wrtoff", labelKey: "ent.arWriteoffs", group: "ent.grp.collections", pk: ["business_unit", "wrtoff_id"], fields: ["business_unit", "wrtoff_id"] },
  { e: "ps_ar_allowance", labelKey: "ent.arAllowance", group: "ent.grp.collections", pk: ["business_unit", "fiscal_year", "accounting_period"], fields: ["business_unit", "fiscal_year", "accounting_period"] },
  { e: "ps_dunning_ltr", labelKey: "ent.dunning", group: "ent.grp.collections", pk: ["setid", "dunning_id", "level_num"], fields: ["setid", "dunning_id", "level_num"] },
  // Restored expense detail (Epic 16.x).
  { e: "ps_ex_travel_auth", labelKey: "ent.travelAuth", group: "ent.grp.expense", pk: ["ex_bu", "ta_id"], fields: ["ex_bu", "ta_id"] },
  { e: "ps_ex_advance_hdr", labelKey: "ent.cashAdvance", group: "ent.grp.expense", pk: ["ex_bu", "ca_id"], fields: ["ex_bu", "ca_id"] },
  // Restored compliance / disclosure (Epic 19.1/19.5).
  { e: "ps_int_audit", labelKey: "ent.auditFindings", group: "ent.grp.compliance", pk: ["finding_id"], fields: ["finding_id", "title", "severity", "status", "material_weakness_flag"] },
  { e: "ps_legal_hold", labelKey: "ent.legalHold", group: "ent.grp.compliance", pk: ["hold_id"], fields: ["hold_id"] },
  { e: "ps_consol_elim", labelKey: "ent.consolidation", group: "ent.grp.compliance", pk: ["consol_group", "fiscal_year", "accounting_period", "elim_seq"], fields: ["consol_group", "fiscal_year", "accounting_period", "elim_seq"] },
];
const ENT_GROUPS = ["ent.grp.master", "ent.grp.collections", "ent.grp.expense", "ent.grp.compliance"];
export function EntitiesPage() {
  const [entity, setEntity] = useState("ps_account");
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [fb, setFb] = useState({ ok: null as boolean | null, msg: "" });
  const meta = ENTITIES.find((x) => x.e === entity)!;
  const load = () => entityRows<Record<string, unknown>>(entity, 100).then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); setCreating(false); setDraft({}); setFb({ ok: null, msg: "" }); /* eslint-disable-next-line */ }, [entity]);
  const cols = rows.length ? Object.keys(rows[0]).filter((k) => !["tenant_id"].includes(k)).slice(0, 6) : meta.fields.slice(0, 6);

  const create = async () => {
    setFb({ ok: null, msg: "" });
    try {
      const body = Object.fromEntries(Object.entries(draft).filter(([, v]) => v !== ""));
      await send("POST", `/api/v1/entities/${entity}`, body, true);
      setFb({ ok: true, msg: t("ent.created") }); setCreating(false); setDraft({}); load();
    } catch (e) { setFb({ ok: false, msg: String((e as Error).message) }); }
  };
  const remove = async (r: Record<string, unknown>) => {
    if (!confirm(t("ent.confirmDelete"))) return;
    const pk = meta.pk.map((k) => String(r[k] ?? "")).join("|");
    try {
      await del(`/api/v1/entities/${entity}/${encodeURIComponent(pk)}`);
      setFb({ ok: true, msg: t("ent.deleted") }); load();
    } catch (e) { setFb({ ok: false, msg: String((e as Error).message) }); }
  };

  return (
    <Page title={t("ent.title")} actions={
      <div className="tabs">
        <Select value={entity} onChange={(e) => setEntity(e.target.value)}>
          {ENT_GROUPS.map((g) => (
            <optgroup key={g} label={t(g)}>
              {ENTITIES.filter((x) => x.group === g).map((x) => <option key={x.e} value={x.e}>{t(x.labelKey)}</option>)}
            </optgroup>
          ))}
        </Select>
        <Button variant="ghost" onClick={() => setCreating((c) => !c)}>{creating ? t("common.cancel") : t("ent.new")}</Button>
      </div>
    }>
      <Feedback ok={fb.ok} message={fb.msg} />
      {creating && (
        <Card title={t("ent.new")}>
          <div className="form-row">
            {meta.fields.map((f) => (
              <Field key={f} label={f}><Input value={draft[f] || ""} onChange={(e) => setDraft((d) => ({ ...d, [f]: e.target.value }))} /></Field>
            ))}
          </div>
          <div className="form-actions"><Button onClick={create}>{t("ent.create")}</Button></div>
        </Card>
      )}
      <Card>
        {rows.length ? (
          <Table head={[...cols, ""]}>
            {rows.map((r, i) => (
              <tr key={i}>
                {cols.map((c) => <td key={c}>{String(r[c] ?? "")}</td>)}
                <td><button className="link-btn danger" onClick={() => remove(r)}>{t("ent.delete")}</button></td>
              </tr>
            ))}
          </Table>
        ) : <Empty>{t("ent.noRows")}</Empty>}
      </Card>
    </Page>
  );
}
